import { getInstance } from './instance';
import { OperatorIdentity, operatorRequestConfig } from './operatorHeaders';

/** Where a manager comes from: the HubSpot owner sync, or added in ABO. */
export type AccountManagerSource = 'hubspot' | 'ds';

export interface AccountManager {
  id: string;
  /**
   * The value stored on a member as its account manager: the HubSpot owner id
   * of a synced manager, `"ds_" + id` of one added in ABO. Same value as
   * `hubspotId`.
   */
  key: string;
  hubspotId: string;
  source: AccountManagerSource;
  /** Active members whose account manager is this manager. */
  memberCount: number;
  email: string;
  firstName: string;
  lastName: string;
  teams: string[];
  isActive: boolean;
  lastSyncedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AccountManagerListResponse {
  total: number;
  data: AccountManager[];
  page: number;
  perPage: number;
  pages: number;
  /**
   * Whether DS still offers the HubSpot roster sync. `false` once the HubSpot
   * master switch is ON: the roster is frozen and maintained by hand. Absent
   * on a DS that predates the switch, which ABO treats as "available" so the
   * two apps can deploy in either order.
   */
  syncAvailable?: boolean;
}

export interface SyncAccountManagersResponse {
  success: boolean;
  synced: number;
  created: number;
  updated: number;
  deactivated: number;
  timestamp: string;
  message?: string;
}

/**
 * DS refusal code of a sync attempted while the HubSpot master switch is ON
 * (HTTP 409). The roster is frozen.
 */
export const ACCOUNT_MANAGERS_SYNC_DISABLED = 'ACCOUNT_MANAGERS_SYNC_DISABLED';

/**
 * DS refusal of a sync whose HubSpot result held no account managers. DS
 * answers HTTP 200 with `success:false` and this code as the `message`, and
 * writes nothing (in both switch positions).
 */
export const ACCOUNT_MANAGERS_SYNC_EMPTY_REFUSED =
  'ACCOUNT_MANAGERS_SYNC_EMPTY_REFUSED';

/**
 * The error DS answers while the sync is disabled: HTTP 409 with
 * `{ code: 'ACCOUNT_MANAGERS_SYNC_DISABLED', message }` as the body.
 */
export interface AccountManagersSyncDisabledError {
  response: {
    status: 409;
    data: { code: typeof ACCOUNT_MANAGERS_SYNC_DISABLED; message?: string };
  };
}

/** A sync response that DS refused because HubSpot returned no account managers. */
export type AccountManagersSyncEmptyRefusedResponse =
  SyncAccountManagersResponse & {
    success: false;
    message: typeof ACCOUNT_MANAGERS_SYNC_EMPTY_REFUSED;
  };

/**
 * True when a sync failed because DS disabled it (409 with
 * `code: 'ACCOUNT_MANAGERS_SYNC_DISABLED'`). Any other status, or a 409 with
 * another code, is a plain failure. Structural on purpose: it reads the axios
 * error shape without depending on `axios.isAxiosError`.
 */
export const isAccountManagersSyncDisabledError = (
  error: unknown,
): error is AccountManagersSyncDisabledError => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) {
    return false;
  }
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (status !== 409 || typeof data !== 'object' || data === null) {
    return false;
  }
  return (data as { code?: unknown }).code === ACCOUNT_MANAGERS_SYNC_DISABLED;
};

/**
 * True when a sync resolved as refused because HubSpot returned no account
 * managers (`success:false`, `message:'ACCOUNT_MANAGERS_SYNC_EMPTY_REFUSED'`).
 * Nothing was changed on DS.
 */
export const isAccountManagersSyncEmptyRefused = (
  result: SyncAccountManagersResponse | null | undefined,
): result is AccountManagersSyncEmptyRefusedResponse =>
  !!result &&
  result.success === false &&
  result.message === ACCOUNT_MANAGERS_SYNC_EMPTY_REFUSED;

/**
 * Page size of `getAllActiveAccountManagers`: the Digital Services page-size
 * ceiling (abo-data-access-guard task 04).
 */
export const ACCOUNT_MANAGERS_MAX_PER_PAGE = 100;

/**
 * Upper bound on the pages `getAllActiveAccountManagers` reads, so a wrong
 * `pages` value can never loop forever (100 pages of 100 managers).
 */
export const ACCOUNT_MANAGERS_MAX_PAGES = 100;

export interface GetAccountManagersParams {
  page?: number;
  perPage?: number;
  activeOnly?: boolean;
}

/** `POST /v1/abo/account-managers`: a first and a last name, and an e-mail. */
export interface CreateAccountManagerRequest {
  fullName: string;
  email: string;
}

/**
 * The typed refusals of the account-manager write routes, each with the HTTP
 * status DS answers it with (DS `account-manager-refusal.ts`). The body is
 * `{ code, message }`.
 */
export const ACCOUNT_MANAGER_OPERATOR_UNVERIFIED =
  'ACCOUNT_MANAGER_OPERATOR_UNVERIFIED';
export const ACCOUNT_MANAGER_FORBIDDEN = 'ACCOUNT_MANAGER_FORBIDDEN';
export const ACCOUNT_MANAGER_UNAVAILABLE = 'ACCOUNT_MANAGER_UNAVAILABLE';
export const ACCOUNT_MANAGER_NOT_FOUND = 'ACCOUNT_MANAGER_NOT_FOUND';
export const ACCOUNT_MANAGER_DUPLICATE_EMAIL =
  'ACCOUNT_MANAGER_DUPLICATE_EMAIL';
export const ACCOUNT_MANAGER_NOT_ACTIVE = 'ACCOUNT_MANAGER_NOT_ACTIVE';
export const ACCOUNT_MANAGER_MEMBER_NOT_FOUND =
  'ACCOUNT_MANAGER_MEMBER_NOT_FOUND';
export const ACCOUNT_MANAGER_COUNT_CHANGED = 'ACCOUNT_MANAGER_COUNT_CHANGED';
export const ACCOUNT_MANAGER_BULK_TOO_LARGE = 'ACCOUNT_MANAGER_BULK_TOO_LARGE';

export const ACCOUNT_MANAGER_REFUSAL_STATUS = {
  [ACCOUNT_MANAGER_OPERATOR_UNVERIFIED]: 401,
  [ACCOUNT_MANAGER_FORBIDDEN]: 403,
  [ACCOUNT_MANAGER_UNAVAILABLE]: 503,
  [ACCOUNT_MANAGER_NOT_FOUND]: 404,
  [ACCOUNT_MANAGER_DUPLICATE_EMAIL]: 409,
  [ACCOUNT_MANAGER_NOT_ACTIVE]: 422,
  [ACCOUNT_MANAGER_MEMBER_NOT_FOUND]: 404,
  [ACCOUNT_MANAGER_COUNT_CHANGED]: 409,
  [ACCOUNT_MANAGER_BULK_TOO_LARGE]: 422,
} as const;

export type AccountManagerRefusalCode =
  keyof typeof ACCOUNT_MANAGER_REFUSAL_STATUS;

export interface AccountManagerRefusal {
  code: AccountManagerRefusalCode;
  status: number;
}

const isAccountManagerRefusalCode = (
  code: unknown,
): code is AccountManagerRefusalCode =>
  typeof code === 'string' &&
  Object.prototype.hasOwnProperty.call(ACCOUNT_MANAGER_REFUSAL_STATUS, code);

/**
 * The typed refusal behind a failed account-manager write, or `null` for any
 * other failure (a validation 400 carries no code). Recognised only when the
 * code is known AND the status is the one DS answers it with. Structural on
 * purpose: it reads the axios error shape without `axios.isAxiosError`.
 */
export const getAccountManagerRefusal = (
  error: unknown,
): AccountManagerRefusal | null => {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) {
    return null;
  }
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const { code } = data as { code?: unknown };
  if (
    !isAccountManagerRefusalCode(code) ||
    ACCOUNT_MANAGER_REFUSAL_STATUS[code] !== status
  ) {
    return null;
  }
  return { code, status: ACCOUNT_MANAGER_REFUSAL_STATUS[code] };
};

export const accountManagersAPI = {
  getAccountManagers: async (
    params: GetAccountManagersParams = {},
  ): Promise<AccountManagerListResponse> => {
    // Digital Services pages start at 1.
    const { page = 1, perPage = 15, activeOnly = true } = params;
    const response = await getInstance().get('/abo/account-managers', {
      params: { page, perPage, activeOnly },
    });
    return response.data;
  },

  /**
   * Every active account manager, read page by page (DS pages start at 1).
   * DS answers `{ data, total, page, perPage, pages }`; a page whose `data` is
   * not an array counts as empty.
   */
  getAllActiveAccountManagers: async (): Promise<AccountManager[]> =>
    readAllAccountManagers(true),

  /**
   * Every account manager, active and inactive (same paging as
   * `getAllActiveAccountManagers`): a member's key can still point at a
   * deactivated manager, whose name must keep resolving.
   */
  getAllAccountManagers: async (): Promise<AccountManager[]> =>
    readAllAccountManagers(false),

  /**
   * Runs the HubSpot roster sync. Rejects with 409
   * `ACCOUNT_MANAGERS_SYNC_DISABLED` once the master switch is ON (see
   * `isAccountManagersSyncDisabledError`); resolves `success:false` with
   * `ACCOUNT_MANAGERS_SYNC_EMPTY_REFUSED` when HubSpot returned no account
   * managers (see `isAccountManagersSyncEmptyRefused`).
   */
  syncFromHubSpot: async (): Promise<SyncAccountManagersResponse> => {
    const response = await getInstance().post('/abo/account-managers/sync');
    return response.data;
  },

  /**
   * Adds an active manager (DS stores it with `source: 'ds'`). DS verifies the
   * operator from the FusionAuth id token in `x-afy-operator-token` and checks
   * `account_manager_admin`; the operator headers go on THIS request only.
   * Refuses with 409 `ACCOUNT_MANAGER_DUPLICATE_EMAIL` when an active manager
   * already has the e-mail (see `getAccountManagerRefusal`).
   */
  createAccountManager: async (
    body: CreateAccountManagerRequest,
    operator: OperatorIdentity,
  ): Promise<AccountManager> => {
    const response = await getInstance().post(
      '/abo/account-managers',
      body,
      operatorRequestConfig(operator),
    );
    return response.data;
  },

  /** Activates or deactivates one manager; same operator check as create. */
  setAccountManagerActive: async (
    id: string,
    isActive: boolean,
    operator: OperatorIdentity,
  ): Promise<AccountManager> => {
    const response = await getInstance().patch(
      `/abo/account-managers/${encodeURIComponent(id)}`,
      { isActive },
      operatorRequestConfig(operator),
    );
    return response.data;
  },
};

const readAllAccountManagers = async (
  activeOnly: boolean,
): Promise<AccountManager[]> => {
  const managers: AccountManager[] = [];
  let page = 1;
  let pages = 1;
  do {
    const response = await accountManagersAPI.getAccountManagers({
      page,
      perPage: ACCOUNT_MANAGERS_MAX_PER_PAGE,
      activeOnly,
    });
    const rows = Array.isArray(response?.data) ? response.data : [];
    managers.push(...rows);
    pages = Number(response?.pages) || 1;
    page += 1;
  } while (page <= pages && page <= ACCOUNT_MANAGERS_MAX_PAGES);
  return managers;
};
